/**
 * UranIA PA — Zod schemas onboarding R&I (US-016 AC#2).
 *
 * Allineati 1:1 con `services/api-gateway/openapi.yaml` v0.4.0 (US-014):
 *   - `OnboardingRiStep1AnagraficaEnte`
 *   - `OnboardingRiStep2Settore`
 *   - `OnboardingRiStep3DominioS3`
 *   - `OnboardingRiStep4TipoEnte` (ENUM `tipo_ente_ri` migration 0002)
 *   - `OnboardingRiStep5TrlProfilo` (ENUM `profilo_tipo_ri` migration 0002)
 *
 * Le regole di forma (lunghezze, pattern, enum) sono replicate qui per
 * dare feedback inline al Beneficiario R&I senza dover battere il server.
 * Quando il backend ritorna 422/409 il messaggio viene mostrato come
 * errore di submit (vedi `OnboardingRiPage` -> `submitError`).
 *
 * Decisione: schemas separati per step (non un grande discriminated
 * union) — stessa motivazione di `pa-schemas.ts` (form indipendenti,
 * RHF resolver non gestisce union con discriminator esterno al payload).
 */

import { z } from 'zod';

// AC OAS: `^[A-Z0-9]{16}$` — codice fiscale italiano (16 char, maiuscolo).
const codiceFiscalePattern = /^[A-Z0-9]{16}$/u;

export const onboardingRiStep1Schema = z.object({
  nome: z.string().min(1).max(120),
  cognome: z.string().min(1).max(120),
  codice_fiscale: z
    .string()
    .min(16)
    .max(16)
    .regex(codiceFiscalePattern, { message: 'codice_fiscale_invalid' }),
  denominazione_ente: z.string().min(1).max(255),
  telefono: z.union([z.string().min(3).max(32), z.literal('')]).optional(),
  codice_ipa_ente: z.union([z.string().min(1).max(64), z.literal('')]).optional(),
});
export type OnboardingRiStep1Body = z.infer<typeof onboardingRiStep1Schema>;

export const onboardingRiStep2Schema = z.object({
  settore_ricerca: z.string().min(1).max(255),
});
export type OnboardingRiStep2Body = z.infer<typeof onboardingRiStep2Schema>;

export const onboardingRiStep3Schema = z.object({
  dominio_s3: z.string().min(1).max(255),
});
export type OnboardingRiStep3Body = z.infer<typeof onboardingRiStep3Schema>;

export const TIPO_ENTE_VALUES = ['universita', 'epr', 'pmi', 'grande_impresa', 'odr'] as const;
export type TipoEnteRi = (typeof TIPO_ENTE_VALUES)[number];

export const onboardingRiStep4Schema = z.object({
  tipo_ente: z.enum(TIPO_ENTE_VALUES),
});
export type OnboardingRiStep4Body = z.infer<typeof onboardingRiStep4Schema>;

export const PROFILO_TIPO_VALUES = ['ricercatore', 'pm', 'cto'] as const;
export type ProfiloTipoRi = (typeof PROFILO_TIPO_VALUES)[number];

export const onboardingRiStep5Schema = z.object({
  trl_range: z.string().min(1).max(64),
  profilo_tipo: z.enum(PROFILO_TIPO_VALUES),
});
export type OnboardingRiStep5Body = z.infer<typeof onboardingRiStep5Schema>;

export interface OnboardingRiSessionDto {
  readonly session_id: string;
  readonly tenant_id: string;
  readonly user_id: string;
  readonly profile_type: 'ri';
  readonly current_step: number;
  readonly total_steps: 5;
  readonly completed_at?: string | null;
}

export interface OnboardingRiStepResponseDto extends OnboardingRiSessionDto {
  readonly outbox_event_id?: string | null;
  readonly audit_event_id?: string | null;
}

export type OnboardingRiStepNumber = 1 | 2 | 3 | 4 | 5;
export const ONBOARDING_RI_TOTAL_STEPS = 5 as const;
